'use strict';

import { cn_pastille } from './cn_pastille';
import { cn_camera } from './cn_camera';
import { cn_add, cn_clone, cn_middle, cn_mul, cn_normal, cn_normalize, cn_sub } from '../utils/cn_utilities';
import { cn_check_input, cn_element_type_input, cn_image_input, cn_detailed_element_type_input } from './cn_inputs';
import { cn_image_dir } from '../utils/image_dir';
import { cn_space } from '../model/cn_space';
import {
    cn_beam,
    cn_column,
    cn_facade,
    cn_facing_trimming,
    CN_FACING_TRIMMING_PLACEMENT_CEILING,
    CN_FACING_TRIMMING_PLACEMENT_FLOOR,
    cn_mmm_composition,
    cn_object_instance,
    cn_opening,
    cn_pipe,
    cn_scene,
    cn_space_segment,
    cn_stairs,
    generate_pattern_for_facing
} from '..';

import { cn_facing } from '../model/cn_facing';
import { cn_roof_opening } from '../model/cn_roof_opening';
import { cn_niche } from '../model/cn_niche';
import {
    CN_FACING_SUPPORTS,
    CN_OPENING_FACING_OPENING,
    CN_OPENING_FACING_SASH,
    CN_OPENING_FACING_SILL,
    CN_OPENING_FACING_TRANSOM,
    CN_OPENING_FACING_CLOSING,
    STAIRS_PARTS
} from '../model/cn_model_constants';
import { cn_facing_family } from '../model/cn_facing_family';

//***********************************************************************************
//***********************************************************************************
//**** cn_pastille facing : a pastille to display a wall or space facing
//***********************************************************************************
//***********************************************************************************

// @ts-ignore
const TITLE_PER_OTHER_TYPE = new Map([
    [cn_beam, 'poutre'],
    [cn_column, 'colonne'],
    [cn_pipe, 'conduit'],
    [cn_stairs, 'escalier'],
    [cn_object_instance, 'équipement'],
]);

// @ts-ignore
const SUPPORT_PER_OTHER_TYPE = new Map([
    [cn_beam, CN_FACING_SUPPORTS.BEAM],
    [cn_column, CN_FACING_SUPPORTS.COLUMN],
    [cn_pipe, CN_FACING_SUPPORTS.PIPE],
    [cn_stairs, CN_FACING_SUPPORTS.STAIR],
    [cn_object_instance, CN_FACING_SUPPORTS.OBJECT],
    [cn_niche, CN_FACING_SUPPORTS.NICHE],
]);

export class cn_pastille_facing extends cn_pastille {
    /**
     * Constructor
     * @param { cn_space_segment | cn_space | cn_facing_trimming | cn_beam | cn_column | cn_pipe | cn_opening | cn_roof_opening | cn_object_instance | cn_stairs } element
     * @param { number } side
     */
    constructor(map, element, side = 0, distance = 30, exterior = false) {
        super()
        this.element = element;
        this.side = side;
        this.rectangle = [30, 30];
        this.distance = distance;
        this._map = map;
        this.title = 'Modifier le revêtement';
        // @ts-ignore
        this.scene = (element.constructor == cn_space_segment) ? element.space.scene : (element.constructor == cn_opening) ? element.wall.scene : element.scene;
        this.exterior = this.scene && this.scene.storey.exterior;
        this.world_position = null;

        if (this.element.constructor == cn_opening || this.element.constructor == cn_roof_opening || this.element.constructor === cn_niche) {
            this.facing_groups = this.element.get_facing_groups();
            this.facing_group = this.facing_groups[side];
        }
    }

    /**
     * Rendering
     * @param {cn_camera} camera
     * @returns {string}
     */
    draw(camera) {
        let html = ''
        let texture = '';
        let color = '#FFFFFF';
        if (this.element.constructor === cn_space_segment) {
            this.position = cn_middle(this.element.points[0], this.element.points[1]);
            const normal = cn_normal(cn_sub(this.element.points[1], this.element.points[0]));
            cn_normalize(normal);
            let nor = cn_mul(normal, this.distance);
            nor[1] = -nor[1];
            this.offset = this.side === 1 ? nor : cn_mul(nor, -1);
            const facing = this.element.facing;
            if (facing) {
                texture = facing.texture;
                color = facing.color || '#FFFFFF';
            }
        } else if (this.element.constructor === cn_space) {
            if (!this.world_position) {
                this.position = cn_add(this.element.center, this.element.label_position);
                if (!this.exterior) {
                    let x_offsets = [-15, 45, -75];
                    this.offset = [x_offsets[this.side], 60];
                    const hint_x_offset = this.offset[0] - 15;
                    const hint_y_offset = this.side != 1 ? this.offset[1] + 25 : this.offset[1] - 30;
                    const hint_pos = cn_add(camera.world_to_screen(this.position), [hint_x_offset, hint_y_offset])
                    html += `<rect x="${hint_pos[0]}" y="${hint_pos[1]}" width="30" height="5" fill="black"></rect>`;
                    if (this.side == 2) {
                        const hint_pos = cn_add(camera.world_to_screen(this.position), [hint_x_offset, this.offset[1] + 35])
                        html += `<rect x="${hint_pos[0]}" y="${hint_pos[1]}" width="30" height="5" fill="black"></rect>`;
                    }
                }
            }
            const facing = this.element.facings[this.side];
            if (facing) {
                texture = facing.texture;
                color = facing.color || '#FFFFFF';
            }
        } else if (this.element.constructor === cn_opening || this.element.constructor === cn_roof_opening || this.element.constructor === cn_niche) {
            return this.draw_opening(this.element, camera);
        } else if (this.element.constructor === cn_stairs) {
            this.position = cn_clone(this.element.get_element_center());
            let facing_codes = this.element.get_facings_codes();
            const facing_index = facing_codes[this.side];
            const facing = this.element.facings[facing_index];
            if (facing) {
                texture = facing.texture;
                color = facing.color || '#FFFFFF';
            }
            this.offset = [0, 36 * (this.side - (facing_codes.length - 1) * 0.5)];

            if (!this.element.similar_facings) {
                let label = STAIRS_PARTS[facing_index].label;
                const hint_x_offset = this.offset[0] + 15;
                const hint_y_offset = this.offset[1] + 0;
                let hint_pos = cn_add(camera.world_to_screen(this.position), [hint_x_offset, hint_y_offset])
                html += camera.draw_text_box_left_screen(hint_pos, 100, 30, label, "dimensionning_text_background", "");
            }
        }
        else {
            if (this.element.constructor === cn_beam || this.element.constructor === cn_pipe) {
                this.position = cn_middle(this.element.vertices[0], this.element.vertices[1])
            } else if (this.element.constructor === cn_object_instance || this.element.constructor === cn_column) {
                this.position = cn_clone(this.element.position)
            } else if (this.element.constructor === cn_facing_trimming) {
                this.position = this.element.get_3d_center();
            } else {
                let box = this.element.get_bounding_box();
                this.position = cn_add(box.posmin, cn_mul(box.size, 0.5));
            }
            // @ts-ignore
            const facing = this.element.facing;
            if (facing) {
                texture = facing.texture;
                color = facing.color || '#FFFFFF';
            }
        }
        if (this.world_position) {
            this.position = this.world_position;
            this.offset = [0, 0];
        }
        this.label = '';
        const p = cn_add(camera.world_to_screen(this.position), this.offset);
        const texture_pattern_and_style = generate_pattern_for_facing(texture, color,
            this.rectangle[0], this.rectangle[1], p[0] - this.rectangle[0] / 2, p[1] - this.rectangle[1] / 2, 'pastille');
        html += texture_pattern_and_style.pattern;
        this.add_attr = texture_pattern_and_style.style;
        html += super.draw(camera);
        return html;
    }

    /**
     * Build callback event
     */
    clicked() {
        if (this.element.constructor === cn_opening || this.element.constructor === cn_roof_opening || this.element.constructor === cn_niche) {
            return this.clicked_opening(this.element);
        }
        let current_facing = null;
        let current_substrate = null;
        let has_substrate = false;
        let is_facade = false;
        if (this.element.constructor === cn_space_segment) {
            is_facade = this.element.is_facade();
            current_facing = this.element.facing;
            current_substrate = this.element.substrate;
            has_substrate = true;
        } else if (this.element.constructor === cn_space) {
            current_facing = this.element.facings[this.side];
            current_substrate = this.element.substrates[this.side];
            has_substrate = true;
        } else if (this.element.constructor === cn_facing_trimming) {
            current_facing = this.element.facing;
            current_substrate = this.element.substrate;
            has_substrate = true;
        } else if (this.element.constructor === cn_stairs) {
            const facing_index = this.element.get_facings_codes()[this.side];
            current_facing = this.element.facings[facing_index];
        } else {
            // @ts-ignore
            current_facing = this.element.facing;
            // @ts-ignore
            has_substrate = (typeof (this.element.substrate) != 'undefined');
            // @ts-ignore
            if (has_substrate) current_substrate = this.element.substrate;
        }
        const input = new cn_element_type_input('Veuillez choisir un revêtement');
        input.default_family = 'Mur intérieur';
        const facings = this._map._building.get_facing_types().concat([]);
        let null_facing = new cn_facing(this._map._building);
        null_facing.is_null = true;
        facings.unshift(null_facing);
        input.helper = `Il n'existe aucun revêtement. Veuillez en créer un depuis le menu.`;
        input.choices = facings;
        input.selection = [facings.findIndex(f => (current_facing && f && current_facing.ID === f.ID) || (!current_facing && !f))];
        if (input.selection[0] < 0) input.selection[0] = 0;

        let substrate_list = [];
        let null_substrate = null;
        if (has_substrate) {
            input.label_d0 = "Revêtement";
            input.label_d1 = "Substrat";
            substrate_list = this._map._building.get_mmm_compositions().concat([]);
            input.choices_d = substrate_list.concat([]);
            null_substrate = new cn_mmm_composition(this._map._building);
            null_substrate.name = "Par défaut";
            null_substrate.is_null = true;
            input.choices_d.unshift(null_substrate);
            input.selection_d = [substrate_list.indexOf(current_substrate) + 1];
        }

        if (this.element.constructor === cn_space_segment) {
            /** @type{cn_space_segment} */
            const space_segment = this.element;
            const wall = space_segment.wall;
            const is_outdoor = space_segment.is_outdoor();
            if (this.exterior) {
                input.options_labels = ['Uniquement à cette clôture', 'Toutes les clôtures du site'];
                input.default_family = 'Mur extérieur';
            } else if (space_segment.wall && space_segment.wall.dormer) {
                if (is_facade)
                    input.options_labels = ['Uniquement à cette façade', 'Toute les façades de ce chien assis', 'Les façades de tous les chiens assis'];
                else
                    input.options_labels = ['Uniquement à ce mur', 'Toute les murs de ce chien assis', 'Les murs de tous les chiens assis'];
                input.default_family = 'Mur extérieur';
            } else if (is_facade) {
                input.options_labels = ['Uniquement à cette façade', 'L\'intégralité cette façade', 'Toutes les façades de ce niveau', 'Toutes les façades du bâtiment'];
                input.default_family = 'Mur extérieur';
            } else {
                input.options_labels = ['Uniquement à ce mur', 'Toute la pièce', 'Toutes les pièces de ce niveau'];
                input.default_family = 'Mur intérieur';
            }
            input.options_selected = input.options_labels[0];

            input.allow_multiple_types = true;
            input.multiple_types_value_label = "Hauteur";
            input.multiple_types = space_segment.upper_facings.map((f, i) => { return { selection: facings.indexOf(f), value: space_segment.upper_heights[i] }; });
            space_segment.upper_substrates.forEach(us => input.choices_d.push(substrate_list.indexOf(us)) + 1);

            input.callback = () => {
                const value = input.selection[0];
                const value_d = input.selection_d[0];
                const option_selected = input.options_labels.indexOf(input.options_selected);
                const new_facing = (input.choices[value] == null_facing) ? null : input.choices[value];
                const new_substrate = (input.choices_d[value_d] == null_substrate) ? null : input.choices_d[value_d];
                const new_upper_heights = [];
                const new_upper_facings = [];
                const new_upper_substrates = [];
                input.multiple_types.forEach((m, i) => {
                    new_upper_heights.push(m.value);
                    let f = input.choices[m.selection];
                    new_upper_facings.push((f == null_facing) ? null : f);
                    let sub = (i + 1 < input.selection_d.length) ? input.choices_d[input.selection_d[i + 1]] : new_substrate;
                    new_upper_substrates.push(sub == null_substrate ? null : sub);
                });
                this._map._building.transaction_manager.push_transaction('Changement de revêtement de murs');
                let space_segments = [];
                if (option_selected > 0) {
                    //**** Exterior walls */
                    if (this.exterior) {
                        const wall_list = this.scene.walls.filter(w => w.has_facings());
                        wall_list.forEach(w => space_segments = space_segments.concat(w.space_segments));
                    }
                    //**** walls of dormers */
                    else if (wall && wall.dormer) {
                        var dormers = (option_selected == 1) ? [wall.dormer] : wall.dormer.parent.roof_dormers;
                        dormers.forEach(dormer => {
                            dormer.walls.forEach(w => space_segments = space_segments.concat(w.space_segments.filter(ss => ss.wall_side == space_segment.wall_side)));
                        })
                    }
                    //**** Facade walls */
                    else if (is_facade) {
                        //*** Apply to the whole facade */
                        if (option_selected == 1) {
                            const facades = this._map._building.get_facades();
                            const facade = cn_facade.find_facade_by_space_segment(space_segment, facades);
                            if (facade)
                                space_segments = space_segments.concat(facade.space_segments);
                        }
                        //*** Apply to all facades of the storey */
                        else if (option_selected == 2)
                            wall.scene.walls.forEach(w => space_segments = space_segments.concat(w.space_segments.filter(ss => ss.is_facade())));
                        //*** Apply to all facades of the building */
                        else {
                            this._map._building.storeys.filter(storey => storey.clone_of == null || storey.clone_of.ID === storey.ID).forEach(storey => {
                                storey.scene.walls.forEach(w => {
                                    for (var side = 0; side < 2; side++)
                                        space_segments = space_segments.concat(w.space_segments.filter(ss => ss.is_facade(storey)));
                                });
                            });
                        }
                    }
                    //**** Non-facade walls */
                    else {
                        //*** Apply to the whole space */
                        if (option_selected == 1) {
                            const space = space_segment.space;
                            space.space_segments.forEach(sslist => space_segments = space_segments.concat(sslist));
                        }
                        //*** Apply to the all spaces */
                        else {
                            space_segment.space.scene.spaces.filter(space => !space.outside).forEach(space => {
                                space.space_segments.forEach(sslist => space_segments = space_segments.concat(sslist));
                            });
                        }
                    }
                } else {
                    space_segments.push(space_segment);
                }
                space_segments.forEach(ss => {
                    this._map._building.transaction_manager.push_item_set(ss, ['facing', 'substrate', 'upper_facings', 'upper_substrates', 'upper_heights']);
                    ss.facing = new_facing;
                    ss.substrate = new_substrate;
                    ss.upper_facings = new_upper_facings;
                    ss.upper_heights = new_upper_heights;
                    ss.upper_substrates = new_upper_substrates;
                });
                this.call('change');
                this._map.refresh();
            };
        } else if (this.element.constructor === cn_space) {
            input.options_labels = ['Uniquement à cette pièce', 'Toutes les pièces de ce niveau']
            input.options_selected = input.options_labels[0];
            input.default_family = (this.side==0)?'Sol intérieur':'Mur intérieur';
            input.callback = () => {
                const value = input.selection[0];
                const value_d = input.selection_d[0];
                let new_facing = input.choices[value];
                if (new_facing.is_null) new_facing = null;
                let new_substrate = input.choices_d[value_d];
                if (new_substrate.is_null) new_substrate = null;
                if (input.options_selected === input.options_labels[0]) {
                    this._map._building.transaction_manager.push_transaction('Changement de revêtement ');
                    this._map._building.transaction_manager.push_item_set(this.element, 'facings');
                    // @ts-ignore
                    this.element['facings'][this.side] = new_facing;
                    this.element['substrates'][this.side] = new_substrate;
                } else {
                    this._map._building.transaction_manager.push_transaction('Changement de revêtement');
                    // @ts-ignore
                    this.scene.spaces.filter(sp => sp.is_indoor() === this.element.is_indoor()).forEach(space => {
                        this._map._building.transaction_manager.push_item_set(space, 'facings');
                        space.facings[this.side] = new_facing;
                        space.substrates[this.side] = new_substrate;
                    });
                }
                this.call('change');
                this._map.refresh();
            }
        } else if (this.element.constructor === cn_facing_trimming) {
            input.default_family = 'Mur extérieur';
            input.callback = () => {
                const value = input.selection[0];
                let new_facing = input.choices[value];
                let new_substrate = input.choices_d[input.selection_d[0]];
                this._map._building.transaction_manager.push_transaction('Changement de revêtement');
                this._map._building.transaction_manager.push_item_set(this.element, ['facing', 'substrate']);
                // @ts-ignore
                this.element.facing = new_facing;
                // @ts-ignore
                this.element.substrate = new_substrate;
                this.call('change');
                this._map.refresh();
            }
        } else if (this.element.constructor === cn_stairs) {
            /** @type {cn_stairs} */
            const stairs = this.element;
            const facing_index = stairs.get_facings_codes()[this.side];
            input.inputs.push(new cn_check_input(`Même revêtement sur toutes les parties de l'escalier`, stairs.similar_facings));
            input.callback = () => {
                const value = input.selection[0];
                const new_facing = input.choices[value];
                const new_similar_facings = input.inputs[0].value;
                this._map._building.transaction_manager.push_transaction('Changement de revêtement');
                this._map._building.transaction_manager.push_item_set(stairs, ["facings", "similar_facings"]);
                if (new_similar_facings)
                    stairs.facings = stairs.facings.map(x => new_facing);
                else
                    stairs.facings[facing_index] = new_facing;
                stairs.similar_facings = new_similar_facings;
                this.call('change');
                this._map.refresh();
            }
        } else {
            input.callback = () => {
                const value = input.selection[0];
                let new_facing = input.choices[value];
                if (new_facing == null_facing) new_facing = null;
                let new_substrate = input.choices_d[input.selection_d[0]];
                if (new_substrate == null_substrate) new_substrate = null;
                this._map._building.transaction_manager.push_transaction('Changement de revêtement');
                this._map._building.transaction_manager.push_item_set(this.element, 'facing');
                // @ts-ignore
                this.element.facing = new_facing;
                if (has_substrate) {
                    this._map._building.transaction_manager.push_item_set(this.element, 'substrate');
                    // @ts-ignore
                    this.element.substrate = new_substrate;
                }
                this.call('change');
                this._map.refresh();

            }
        }
        this._map.call('element_type_input', input);
    }

    /**
     *
     * @param {cn_opening | cn_roof_opening} element
     * @param {cn_camera} camera
     * @returns
     */
    draw_opening(element, camera) {
        let html = ''
        let p0 = [0, 0];
        let p1 = [0, 0];
        if (element.constructor === cn_opening) {
            const v0 = cn_mul(element.wall.bounds.direction, element.position);
            const v1 = cn_mul(element.wall.bounds.direction, element.position + element.get_width());
            p0 = cn_add(element.wall.bounds.pmin, v0);
            p1 = cn_add(element.wall.bounds.pmin, v1);
            const p2 = cn_add(element.wall.bounds.pmax, v1);
            this.position = cn_middle(p0, p2);
        }
        else {
            let box = element.get_bounding_box();
            this.position = cn_add(box.posmin, cn_mul(box.size, 0.5));
            p0 = this.position;
            p1 = cn_add(p0, [1, 0]);
        }

        if (this.facing_groups.length == 0) {
            this.offset = [0, 0];
        }
        else {
            const screen_dir = cn_sub(camera.world_to_screen(p1), camera.world_to_screen(p0));
            cn_normalize(screen_dir);
            let screen_nor = cn_mul(cn_normal(screen_dir), -50);
            if (this.facing_group.side == 0) screen_nor = cn_mul(screen_nor, -1);

            let same_side_facing_groups = this.facing_groups.filter(f => f.side == this.facing_group.side);

            this.offset = cn_add(screen_nor, cn_mul(screen_dir, 50 * (same_side_facing_groups.indexOf(this.facing_group) - (same_side_facing_groups.length - 1) * 0.5)));
        }

        if (this.facing_group.code == "hole") {
            const hint_x_offset = this.offset[0] - 20;
            const hint_y_offset = this.offset[1] + 20;
            const hint_pos = cn_add(camera.world_to_screen(this.position), [hint_x_offset, hint_y_offset])
            html += `<path d="M ${hint_pos[0]} ${hint_pos[1]} h 40 v -40 h -40 Z"
                fill="transparent" stroke="black" stroke-width="3" />`
        }

        let facing_list = [];
        this.facing_group.roles.forEach(role => {
            let facing = element.facings[this.facing_group.side][role.code];
            if (typeof (facing) == 'undefined') return;
            if (!facing_list.includes(facing)) facing_list.push(facing)
        });

        const facing = (facing_list.length > 0) ? facing_list[0] : null;
        let texture = '';
        let color = '#FFFFFF';
        if (facing) {
            texture = facing.texture;
            color = facing.color || '#FFFFFF';
        }
        this.incoherent = facing_list.length > 1;

        const p = cn_add(camera.world_to_screen(this.position), this.offset);
        const texture_pattern_and_style = generate_pattern_for_facing(texture, color,
            this.rectangle[0], this.rectangle[1], p[0] - this.rectangle[0] / 2, p[1] - this.rectangle[1] / 2, 'pastille');
        html += texture_pattern_and_style.pattern;
        this.add_attr = texture_pattern_and_style.style;
        html += super.draw(camera);
        return html;
    }

    /**
     * Specific method to edit an opening
     * @param {cn_opening | cn_roof_opening | cn_niche} element
     * @returns
     */
    clicked_opening(element) {
        let input = cn_pastille_facing.generic_detailed_element_type_input(this._map);
        input.label = this.facing_group.label;

        let null_facing = input["null_facing"];
        let null_substrate = input["null_substrate"];

        input.checkbox_label = (this.facing_group.roles.length > 1) ? "Tous identiques" : "";
        input.checkbox_status = true;
        let default_facing = undefined;
        let default_substrate = undefined;
        this.facing_group.roles.forEach(role => {
            if (!input.checkbox_status) return;
            let facing = element.facings[this.facing_group.side][role.code];
            if (typeof (facing) != 'undefined') {
                if (default_facing === undefined)
                    default_facing = facing;
                else if (default_facing != facing)
                    input.checkbox_status = false;
            }
            let substrate = element.substrates[this.facing_group.side][role.code];
            if (typeof (substrate) != 'undefined') {
                if (default_substrate === undefined)
                    default_substrate = substrate;
                else if (default_substrate != substrate)
                    input.checkbox_status = false;
            }
        });
        if (default_facing == undefined || !input.checkbox_status)
            default_facing = null;
        if (default_substrate == undefined || !input.checkbox_status)
            default_substrate = null;
        input.selection = this.facing_group.roles.map(role => {
            let selection = {
                label: role.label,
                code: role.code,
                element_types: []
            };
            selection.element_types.push(element.facings[this.facing_group.side][role.code]);
            selection.element_types.push(element.substrates[this.facing_group.side][role.code]);
            if (typeof (selection.element_types[0]) == 'undefined') selection.element_types[0] = default_facing;
            if (typeof (selection.element_types[1]) == 'undefined') selection.element_types[1] = default_substrate;
            if (selection.element_types[0] == null) selection.element_types[0] = null_facing;
            if (selection.element_types[1] == null) selection.element_types[1] = null_substrate;
            return selection;
        });

        const option_space = (this.scene && this.scene.constructor == cn_scene && element.constructor == cn_opening && element.wall_interface) ? element.wall_interface.spaces[this.facing_group.side] : null;
        let opening_category = "";
        let other_openings = [];
        if (option_space) {
            opening_category = (element.constructor == cn_opening || element.constructor == cn_roof_opening) ? element.opening_type.category : "";
            other_openings = this.scene.walls.flatMap(w => w.openings).filter(op => op.valid && op.opening_type.category == opening_category && op.wall_interface && (op.wall_interface.spaces.includes(option_space)));
            if (this.facing_group.code != "hole") other_openings = other_openings.filter(op => !op.opening_type.free);
        }

        if (other_openings.length > 1) {
            if (opening_category == "window")
                input.options_labels = ['Uniquement à cette fenêtre', `Toutes les fenêtres de ${option_space.get_name(this.scene.storey)}`];
            else
                input.options_labels = ['Uniquement à cette porte', `Toutes les portes de ${option_space.get_name(this.scene.storey)}`];
            input.options_selected = 0;
        }

        input.callback = () => {
            let openings = (other_openings.length > 1 && input.options_selected > 0) ? other_openings : [element];
            this._map._building.transaction_manager.push_transaction('Changement de revêtement de baie');
            input.selection.forEach(sel => {
                let new_facing = sel.element_types[0];
                if (new_facing.is_null) new_facing = null;
                let new_substrate = sel.element_types[1];
                if (new_substrate.is_null) new_substrate = null;
                openings.forEach(op => {
                    this._map._building.transaction_manager.push_item_set(op, ["facings","substrates"]);
                    let applied_side = this.facing_group.side;
                    if (op != element && option_space && op.wall_interface && op.wall_interface.spaces.includes(option_space)) {
                        applied_side = op.wall_interface.spaces.indexOf(option_space);
                    }
                    op.facings[applied_side][sel.code] = new_facing;
                    op.substrates[applied_side][sel.code] = new_substrate;
                })
            });
        };

        this._map.call('detailed_element_type_input', input);
    }

    static generic_detailed_element_type_input(map) {
        let input = new cn_detailed_element_type_input("");

        let null_facing = new cn_facing(map._building);
        null_facing.is_null = true;
        null_facing.name = "Aucun";

        let null_substrate = new cn_mmm_composition(map._building);
        null_substrate.is_null = true;
        null_substrate.name = "Par défaut";

        input["null_facing"] = null_facing;
        input["null_substrate"] = null_substrate;

        input.change_element_type_callback = (sel, index, done_method = null) => {
            let choice_input = new cn_element_type_input();
            choice_input.default_family = 'Mur intérieur';
            if (index == 0) {
                choice_input.label = "Choix du revêtement:";
                choice_input.choices = [null_facing, ...map._building.get_facing_types()];
            }
            else if (index == 1) {
                choice_input.label = "Choix du substrat:";
                choice_input.choices =[null_substrate, ...map._building.get_mmm_compositions()];
            }
            else return;
            choice_input.selection = [choice_input.choices.indexOf(sel.element_types[index])];
            choice_input.callback = () => {
                sel.element_types[index] = choice_input.choices[choice_input.selection[0]];
                if (done_method) done_method();
            };
            map.call('element_type_input', choice_input);
        };


        return input;
    }
}
